import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Alert, Button, CircularProgress } from "@mui/material";
import { Image as ImageIcon } from "react-feather";
import { withCustomerAuth } from "../../../admin/components/GetAuth";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import { fetchOrder, formatPrice, ORDER_PROGRESS_COLOR, PAYMENT_METHODS } from "../../../frontend/pages/shop/shopApi";
import styles from "../../../frontend/pages/shop/shop.module.css";
import CustomerShell from "./CustomerShell";

const STEPS = ["Placed", "Confirmed", "Out For Delivery", "Delivered"];

const OrderDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { websiteData } = useWebsite();
  useSiteBranding("Order details", websiteData);
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchOrder(id).then((response) => {
      if (response.data.status === "SUCCESS") setOrder(response.data.data);
      else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [id]);

  const back = <Button component={Link} to="/customer/order-history" className={styles.SecondaryBU}>All orders</Button>;

  if (notFound) {
    return (<CustomerShell eyebrow="Shop Now" title="Order not found" actions={back}><div className={`${styles.Panel} ${styles.Empty}`}>We could not find this order.</div></CustomerShell>);
  }
  if (!order) {
    return (<CustomerShell eyebrow="Shop Now" title="Order"><div className={styles.Empty}><CircularProgress size={28} /></div></CustomerShell>);
  }

  const reached = STEPS.indexOf(order.progress);
  const due = Number(order.total) - Number(order.paid_amount);

  return (<CustomerShell eyebrow="Shop Now" title={`Order #${order.order_no}`} sub={`Placed ${dayjs(order.created_at).format("DD MMM YYYY, HH:mm")}`} actions={back}>
    <div className={styles.Embedded}>
      {location.state?.placed && <Alert severity="success" style={{ marginBottom: 16 }}>Thank you, your order has been placed. We will deliver it on {dayjs(order.delivery_date).format("dddd D MMMM")}.</Alert>}

      <div className={styles.Panel} style={{ marginBottom: 16 }}>
        {order.progress === "Cancelled"
          ? <span className={styles.StatusPill} style={{ background: ORDER_PROGRESS_COLOR.Cancelled }}>Cancelled</span>
          : (<div className={styles.Steps}>
            {STEPS.map((s, i) => <div key={s} className={`${styles.Step} ${i <= reached ? styles.StepDone : ""}`}>{s}</div>)}
          </div>)}
        <div className={styles.LineMeta}>Delivery date {order.delivery_date ? dayjs(order.delivery_date).format("DD MMM YYYY") : "-"}</div>
      </div>

      <div className={styles.TwoCol}>
        <div className={styles.Panel}>
          <h2 className={styles.PanelTitle}>Items</h2>
          {order.items?.map((item) => (<div className={styles.CartLine} key={item.id}>
            {item.product?.image ? <img src={item.product.image} alt="" className={styles.CartThumb} /> : <span className={`${styles.CartThumb} ${styles.NoImage}`}><ImageIcon size={22} /></span>}
            <div>
              <div className={styles.LineTitle}>{item.title}</div>
              <div className={styles.LineMeta}>{item.quantity} × {formatPrice(item.price)}</div>
            </div>
            <strong>{formatPrice(item.sub_amount)}</strong>
          </div>))}
        </div>

        <div>
          <div className={styles.Panel} style={{ marginBottom: 16 }}>
            <h2 className={styles.PanelTitle}>Summary</h2>
            <div className={styles.SumRow}><span>Items</span><span>{formatPrice(order.subtotal)}</span></div>
            <div className={styles.SumRow}><span>Delivery</span><span>{formatPrice(order.delivery_charge)}</span></div>
            <div className={`${styles.SumRow} ${styles.SumTotal}`}><span>Total</span><span>{formatPrice(order.total)}</span></div>
            <div className={styles.SumRow}><span>Payment</span><span>{PAYMENT_METHODS.find((m) => m.value === order.payment_method)?.label || order.payment_method}</span></div>
            <div className={styles.SumRow}><span>{order.payment_status === 1 ? "Paid" : "To pay"}</span><span>{formatPrice(order.payment_status === 1 ? order.paid_amount : due)}</span></div>
          </div>
          <div className={styles.Panel}>
            <h2 className={styles.PanelTitle}>Delivery address</h2>
            <div><strong>{order.delivery_address?.name}</strong></div>
            <div className={styles.LineMeta}>{order.delivery_address?.address}{order.delivery_address?.post_code ? `, ${order.delivery_address.post_code}` : ""}</div>
            <div className={styles.LineMeta}>{order.delivery_address?.phone}</div>
            {order.notes && <p className={styles.Hint} style={{ marginTop: 10 }}>Notes: {order.notes}</p>}
          </div>
        </div>
      </div>
    </div>
  </CustomerShell>);
};

export default withCustomerAuth(OrderDetail);
